import { useEffect, useState } from 'react';

import { Badge, EmptyState, Loading, TextInput } from '../../components/common';
import { useAttributeSet, useAttributeSets } from '../../hooks/useAttributeSet';
import '../pages.css';

/**
 * Browse the attribute vocabularies.
 *
 * Read-only on purpose: an attribute set is a record of what a model was trained
 * against, and editing one in the UI would silently break every version bound to
 * it. Regenerating from the training repo (`scripts/export_attribute_set.py`) is
 * the supported way to change one.
 */
export function AttributesTab() {
  const { sets, loading } = useAttributeSets();
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState('');
  const { attributeSet, loading: loadingSet } = useAttributeSet(selectedId);

  useEffect(() => {
    if (!selectedId && sets.length) setSelectedId(sets[0].id);
  }, [selectedId, sets]);

  if (loading) return <Loading label="Loading attribute sets…" />;
  if (!sets.length) {
    return (
      <EmptyState glyph="▤" title="No attribute sets installed">
        Generate one from the training repo with{' '}
        <span className="mono">scripts/export_attribute_set.py</span>.
      </EmptyState>
    );
  }

  const needle = filter.trim().toLowerCase();
  const attributes = (attributeSet?.attributes || []).filter(
    (attribute) =>
      !needle ||
      attribute.name.toLowerCase().includes(needle) ||
      attribute.values.some((value) => value.toLowerCase().includes(needle)),
  );

  const groups = attributes.reduce((accumulator, attribute) => {
    const key = attribute.group || 'Other';
    accumulator.set(key, [...(accumulator.get(key) || []), attribute]);
    return accumulator;
  }, new Map());

  return (
    <div className="attr-browser">
      <div className="list-panel">
        {sets.map((set) => (
          <button
            key={set.id}
            type="button"
            className={`list-item ${set.id === selectedId ? 'list-item--active' : ''}`}
            onClick={() => setSelectedId(set.id)}
          >
            <div className="list-item__title">{set.label}</div>
            <div className="list-item__meta">
              {set.attribute_count} attributes · {set.response_scheme} · {set.groups.length} groups
            </div>
          </button>
        ))}
      </div>

      <section>
        {loadingSet ? (
          <Loading />
        ) : attributeSet ? (
          <>
            <div className="row" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
              <Badge tone="accent">{attributeSet.attribute_count} attributes</Badge>
              <Badge>{attributeSet.response_scheme}</Badge>
              <Badge>{attributeSet.detail_level}</Badge>
              <div className="grow" />
              <div style={{ width: 240 }}>
                <TextInput
                  value={filter}
                  placeholder="Filter attributes or values…"
                  onChange={(event) => setFilter(event.target.value)}
                />
              </div>
            </div>

            {attributeSet.notes && (
              <div className="card card--pad small muted" style={{ marginBottom: 14 }}>
                {attributeSet.notes}
              </div>
            )}

            {[...groups.entries()].map(([group, items]) => (
              <div key={group} style={{ marginBottom: 18 }}>
                <div className="section-label" style={{ marginBottom: 6 }}>
                  {group} · {items.length}
                </div>
                <div className="card">
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{ width: '32%' }}>Attribute</th>
                        <th>Values (index order)</th>
                        <th style={{ width: 90 }}>Typical</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((attribute) => (
                        <tr key={attribute.name} style={{ cursor: 'default' }}>
                          <td>
                            <div>{attribute.name}</div>
                            {attribute.description && (
                              <div className="xs subtle" style={{ marginTop: 2 }}>
                                {attribute.description}
                              </div>
                            )}
                          </td>
                          <td>
                            <div className="row" style={{ flexWrap: 'wrap', gap: 4 }}>
                              {attribute.values.map((value, index) => (
                                <span key={value} className="chip" title={`class index ${index}`}>
                                  <span className="subtle">{index}</span> {value}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="muted small">{attribute.most_common || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}

            {attributes.length === 0 && (
              <EmptyState glyph="⌕" title="Nothing matches that filter" />
            )}
          </>
        ) : null}
      </section>
    </div>
  );
}
